import { AmericanoRules, CustomFormatBlock, ITournamentFormat, KnockoutRules, LeagueRules } from "../models/ITournament";
import { ILocation } from "../models/ILocation";

export type AddTournamentDTO = {
  name: string;
  description: string;
  type: string;
  startDate: string;
  endDate: string;
  locationId?: string;
  specificDates?: Date[];
  cover: string;
  tournamentFeatures: string[];
  seasonId?: string;
};

export type AddTournamentCategoryDTO = {
  name: string;
  startDate?: string;
  endDate?: string;
  requireConsent: boolean;
  isIncludedInRanking?: boolean;
  format: string;
  formatBlocks?: CustomFormatBlock[];
  maxMen: number;
  maxPlayers: number;
  maxWomen: number;
  minMen: number;
  minWomen: number;
  spotsNum: number;
  femaleBonusPoints?: number;
  femaleBonusCap?: number;
  allowIndividualRegistration?: boolean;
  allowTeamRegistration?: boolean;
  /** Players per team in a Both category; reads return the effective size (the format's unit size wins). */
  teamSize?: number | null;
  /**
   * The fewest players a premade team may register with in a Both category (spec 2026-10-30). Reads return the
   * effective minimum (the team size when teams must be complete); null when not Both. Writes: null = exactly
   * the team size.
   */
  teamMinSize?: number | null;
  teamMaxMen?: number | null;
  teamMaxWomen?: number | null;
  fees: {
    amount?: number;
    isEnabled: boolean;
    currency: string;
    perTeam: boolean;
    description: string;
    startDate?: string | null;
    endDate?: string | null;
  }[];
  prizes: {
    rank: number;
    amount?: number;
    currency: string;
    gift: string;
  }[];
  awards: {
    name: string;
  }[];
};

export type AddCategoryDTO = {
  name: string;
  startDate: string;
  endDate: string;
  format: ITournamentFormat;
  spotsNum: number;
  maxPlayers: number;
  minMen: number;
  maxMen: number;
  minWomen: number;
  maxWomen: number;
  femaleBonusPoints?: number;
  femaleBonusCap?: number;
  isIncludedInRanking?: boolean;
  allowIndividualRegistration?: boolean;
  allowTeamRegistration?: boolean;
  /** Players per team in a Both category; reads return the effective size (the format's unit size wins). */
  teamSize?: number | null;
  /**
   * The fewest players a premade team may register with in a Both category (spec 2026-10-30). Reads return the
   * effective minimum (the team size when teams must be complete); null when not Both. Writes: null = exactly
   * the team size.
   */
  teamMinSize?: number | null;
  teamMaxMen?: number | null;
  teamMaxWomen?: number | null;
  fees: AddFeeDTO[];
  prizes: AddPrizeDTO[];
  awards: AddAwardDTO[];
  customFormatBlocks?: CustomFormatBlock[];
};

export type EditTournamentDTO = {
  name?: string;
  type?: string;
  isPublished?: boolean;
  cover?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  specificDates?: Date[];
  locationId?: string | null;
  registrationOpen?: boolean;
  registrationClosed?: boolean;
  seasonId?: string;
  // Clear semantics: omit the key to leave the column unchanged, send "" to
  // clear it to NULL. Never send null.
  paymentMethodLabel?: string;
  paymentInstructions?: string;
};


export type TournamentResponseDTO = {
  cover: string;
  createdAt: string;
  createdBy: string;
  description: string;
  endDate: string;
  features: string[];
  id: string;
  locationId: string | null;
  location: ILocation | null;
  name: string;
  registrationClosed: boolean;
  registrationOpen: boolean;
  startDate: string;
  type: { id: string; typeName: string };
  typeId: string;
  seasonId?: string;
  paymentMethodLabel?: string | null;
  paymentInstructions?: string | null;
};

export type AddPrizeDTO = {
  rank: number;
  amount?: number;
  currency: string;
  gift: string;
  prizeOption: string;
  rankingPoints?: number;
  clashPoints?: number;
  added: boolean;
  edit?: boolean;
};

export type AddAwardDTO = {
  id: string;
  label: string;
  rankingPoints?: number;
  clashPoints?: number;
  added: boolean;
  edit?: boolean;
};

export type AddFeeDTO = {
  id: string;
  perTeam: boolean;
  currency: string;
  amount?: number;
  description: string;
  added: boolean;
  edit?: boolean;
  startDate?: string | null;
  endDate?: string | null;
};

export type EditTournamentCategoryDTO = {
  name?: string;
  startDate?: string;
  endDate?: string;
  arenaPublished?: boolean;
  isIncludedInRanking?: boolean;
  allowIndividualRegistration?: boolean;
  allowTeamRegistration?: boolean;
  /** Players per team in a Both category; reads return the effective size (the format's unit size wins). */
  teamSize?: number | null;
  /**
   * The fewest players a premade team may register with in a Both category (spec 2026-10-30). Reads return the
   * effective minimum (the team size when teams must be complete); null when not Both. Writes: null = exactly
   * the team size.
   */
  teamMinSize?: number | null;
  teamMaxMen?: number | null;
  teamMaxWomen?: number | null;
  format?: {
    isRankingBlock?: boolean;
    encounters?: number;
    mode?: string;
    rankingCriteria?: string;
    thirdPlace?: boolean;
    type: string;
    winPts?: number;
    lostPts?: number;
    drawPts?: number;
    pointsPerMatch?: number;
    roundsCount?: number;
    unitSize?: number;
    mixedPairing?: boolean;
    /** Roulette form field; sent as americanoRules.unitsPerSide = playersPerSide / unitSize. */
    playersPerSide?: number;
  };
  customFormatBlocks?: CustomFormatBlock[];
  maxMen?: number;
  maxPlayers?: number;
  minMen?: number;
  minWomen?: number;
  maxWomen?: number;
  spotsNum?: number;
  femaleBonusPoints?: number;
  femaleBonusCap?: number;
  registrationOpen?: boolean;
  registrationClosed?: boolean;
  fees?: {
    id: string;
    perTeam: boolean;
    currency: string;
    amount?: number;
    description: string;
    isEnabled: boolean;
    added?: boolean;
    edit?: boolean;
    startDate?: string | null;
    endDate?: string | null;
  }[];
  prizes?: {
    id: string;
    rank: number;
    prizeOption?: string;
    amount?: number;
    currency: string;
    gift: string;
    rankingPoints?: number;
    clashPoints?: number;
    added?: boolean;
    edit?: boolean;
  }[];
  awards?: {
    id: string;
    name: string;
    playerId: string;
    rankingPoints?: number;
    clashPoints?: number;
    includedInVoting?: boolean;
    added?: boolean;
    edit?: boolean;
  }[];
};

export type StageDetails = {
  id: string;
  startDate: string;
  endDate: string;
  formatName: string;
  aliasName: string;
  knockoutRules: KnockoutRules;
  leagueRules: LeagueRules;
  americanoRules?: AmericanoRules;
  order: number;
  teams: { id: string; teamName: string; captainName: string }[];
  tournamentName: string;
  categoryName: string;
  precedingStages: IPrecedingStage[];
  hasEmptyTeams?: boolean;
  qualificationRules?: {
    precedingStageId: string;
    fromPosition: number;
    toPosition: number;
  }[];
};

export type IPrecedingStage = {
  id: string;
  stageName: string;
  stageOrder: number;
  leagueRules: LeagueRules;
  knockoutRules: KnockoutRules;
  isStarted: boolean;
  isEnded: boolean;
  totalTeams: number;
  totalGroups: number;
  teamsPerGroup: { [groupName: string]: number };
  value: number;
  // Position range for tier-based qualification (e.g., positions 1-2 for Tier A, 3-4 for Tier B)
  fromPosition?: number;
  toPosition?: number;
};

export type IMatchFilters = {
  venues: string[];
  rounds: string[];
  brackets: string[];
  teams: string[];
};

export enum PaymentType {
  BankTransfer = 1,
  Cheque = 2,
  Cash = 3,
  Instapay = 4,
}

export type UpdatePrizePaymentRequestDTO = {
  tournamentPrizeId: string;
  teamId: string;
  paymentType: PaymentType;
  paymentReference?: string;
  paymentDate?: string;
  notes?: string;
  isPaid: boolean;
};

export type RegistrationGateDTO = {
  state: number; // 0 = Open, 1 = NotOpenYet, 2 = Closed (serialized as integer by .NET)
  reopensAt?: string | null;
  message?: string | null;
};

// Individual Registration Types
export type IndividualRegistrationDTO = {
  // Either an existing account (playerId) OR a non-registered "guest" (playerName only).
  playerId?: string;
  playerName?: string;
  feeId?: string;
  promoCode?: string;
};

export type IndividualRegistrationResponse = {
  id: string;
  playerId: string;
  playerName: string;
  status: string;
  registeredAt: string;
};

export type GenderSpots = {
  max: number;
  filled: number;
  available: number;
};

export type AvailableSpotsDTO = {
  totalSpots: number;
  filledSpots: number;
  availableSpots: number;
  maleSpots?: GenderSpots;
  femaleSpots?: GenderSpots;
};

// ── Tournament Report ──
// Single payload returned by GET /Tournament/{id}/report — drives the dashboard
// reports page so the FE doesn't fan out across multiple endpoints.
export type TournamentReportSummaryDTO = {
  teams: number;
  players: number;
  confirmed: number;
  pending: number;
  totalPaid: number;
  pendingAmount: number;
  expectedTotal: number;
  totalExpenses: number;
  netRevenue: number;
  /** The registrations' fee currency when they all share one; null => EGP. */
  currency?: string | null;
};

export type TournamentReportExpenseDTO = {
  id: string;
  source: "CourtReservation" | "Manual";
  categoryId: string | null;
  categoryName: string | null;
  // Court-reservation rows
  courtName: string | null;
  startTime: string | null;
  endTime: string | null;
  status: string | null;
  paymentMethod: string | null;
  // Manual-expense rows
  description: string | null;
  expenseDate: string | null;
  // Shared
  cost: number;
  notes: string | null;
};

export type TournamentReportRegistrationDTO = {
  teamMemberId: string;
  playerId: string | null;
  playerName: string;
  teamId: string;
  teamName: string;
  categoryName: string;
  registrationStatus: string;
  paymentStatus: string;
  consent: boolean;
  /** What this registration owes: price after promo; a per-team fee sits on one row, 0 on teammates. */
  feeAmount: number;
  /** The fee's list price when it was assigned (before promo); null without a fee. */
  feePrice?: number | null;
  feeCurrency?: string | null;
  feePerTeam?: boolean;
  feeDescription: string | null;
  promoCode: string | null;
  paymentMethod: string | null;
  fawryReferenceNumber: string | null;
  receiptScreenshot: string | null;
  email: string | null;
  phoneNumber: string | null;
  nationality: string | null;
  gender: string | null;
  isCaptain: boolean;
  addedAt: string;
};

export type TournamentReportPaymentDTO = {
  source: "Online" | "InstaPay" | "Balance" | "Cash";
  id: string;
  playerName: string;
  teamName: string;
  categoryName: string | null;
  payForTeam: boolean;
  teamMemberCount: number | null;
  amount: number;
  currency?: string | null;
  paymentStatus: string;
  registrationStatus: string | null;
  paymentMethod: string | null;
  reference: string | null;
  receiptScreenshot: string | null;
  createdAt: string;
};

export type TournamentReportDTO = {
  tournamentId: string;
  tournamentName: string;
  startDate: string;
  endDate: string;
  locationName: string | null;
  summary: TournamentReportSummaryDTO;
  registrations: TournamentReportRegistrationDTO[];
  payments: TournamentReportPaymentDTO[];
  expenses: TournamentReportExpenseDTO[];
};
